import { useRescueDecision } from "../../hooks/useRescueDecision";
import { Sparkles } from "lucide-react";
import type { RescueCase } from "@io-spoke/contracts/rescue";
import { RecoveryDecisionPanel } from "./RecoveryDecisionPanel";

type Props = {
  rescueCase: RescueCase;
  onCompleted?: (entityId: string) => void;
  onOpenWork: (id: string) => void | Promise<void>;
};

export function RecoveryPlanAnswer({
  rescueCase,
  onCompleted,
  onOpenWork,
}: Props) {
  const decisionState = useRescueDecision(rescueCase.id, onCompleted);
  const snapshot =
    decisionState.decision?.status === "pending" ||
    decisionState.decision?.status === "completed";
  const needsFreshPlan =
    decisionState.decision?.status === "stale" ||
    decisionState.decision?.status === "expired";
  let plan = rescueCase.plan;
  if (snapshot) plan = decisionState.decision!.plan;
  if (needsFreshPlan) plan = [];
  const activeWorkId =
    rescueCase.status === "active" ? rescueCase.launchedEntityId : null;
  const answer = activeWorkId
    ? "This rescue is active. You can open the recovery work or review the plan below."
    : "Here’s the proposed next step. Review the plan, then save it to confirm exactly what work you want to start.";
  return (
    <div
      id="rescue-plan-answer"
      role="region"
      aria-label="Recovery plan review"
      className="min-w-0"
    >
      <p className="px-5 pt-5 text-sm leading-6 sm:px-6">{answer}</p>
      <div className="bg-muted/15 p-5 sm:p-6">
        <div className="mb-3 flex items-center gap-2">
          <Sparkles
            className="h-4 w-4 text-violet-600 dark:text-violet-400"
            aria-hidden="true"
          />
          <h3 className="text-sm font-semibold">
            {snapshot ? "Plan saved for this decision" : "Recovery plan"}
          </h3>
        </div>
        <div className="overflow-hidden rounded-md border bg-card">
          {needsFreshPlan ? (
            <p className="p-4 text-sm text-muted-foreground">
              Save a fresh plan to load its latest snapshot. Review that
              snapshot before approval.
            </p>
          ) : null}
          {!needsFreshPlan && plan.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              No recovery steps are recorded for this case yet.
            </p>
          ) : null}
          {plan.map((step, index) => (
            <div
              key={step.id}
              className="flex gap-3 border-b p-3.5 last:border-b-0"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-violet-100 text-xs font-semibold text-violet-700 dark:bg-violet-950/50 dark:text-violet-300">
                {index + 1}
              </span>
              <div className="min-w-0 break-words">
                <h4 className="text-sm font-medium">{step.title}</h4>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  {step.description}
                </p>
                <p className="mt-2 text-xs font-medium text-muted-foreground">
                  {step.owner} · {step.timing}
                </p>
              </div>
            </div>
          ))}
        </div>
        <RecoveryDecisionPanel
          key={rescueCase.id}
          rescueCase={rescueCase}
          onOpenWork={onOpenWork}
          state={decisionState}
        />
      </div>
    </div>
  );
}
